import React, { useMemo } from "react";

import { useSelector } from "react-redux";

import AgGrid from "components/AgGrid/AgGrid";
import { ModalTableWrapper } from "components/AgGrid/TableWrapper";
import { globalDefaultColDef } from "components/AgGrid/AgGridContainer";
import { Button, SecondaryButton } from "components/Form/Button";
import { Column, Row } from "components/Layout";
import { BoldText, Subtitle, Text } from "components/Text";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  ZoomyDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import { DeploymentBusinessName } from "components/ClearingSaleCards/BusinessComponents";

import { ApiModel } from "constants/loading";

import { formatDollarsFromCents } from "lib";

import { ModalTypes } from "constants/navigation";
import { BuyerPaymentSummaryLotColumnDefs } from "constants/columnDefinitions";

import {
  selectBuyerBalanceSummaryByBuyerId,
  BuyerPaymentSummaryData,
  BuyerPaymentSummaryItem,
} from "selectors/clearingSaleInvoice";
import { getContextByModalType } from "selectors";

import { BusinessPaymentsTable } from "components/BusinessPaymentsModal/BusinessPaymentsTable";

const getRowId = (params: { data: BuyerPaymentSummaryItem }) =>
  params.data.lotId;

interface BuyerPaymentSummaryModalProps {
  onClose: (event?: any, target?: any) => void;
}

type BreakDownItem = { label: string; amount: string };

type BuyerPaymentSummaryModalContext = {
  buyerId: string;
} | null;

const selectBuyerPaymentSummaryModalContext = getContextByModalType(
  ModalTypes.BuyerPaymentSummary,
) as (state: State) => BuyerPaymentSummaryModalContext;

const formatCurrency = (cents: number): string =>
  formatDollarsFromCents(cents, { format: true });

const EMPTY_SUMMARY_DATA: BuyerPaymentSummaryData = {
  items: [],
  totals: {
    amountExclCents: 0,
    feesCents: 0,
    taxCents: 0,
    dueCents: 0,
  },
};

export function BuyerPaymentSummaryModal({
  onClose,
}: BuyerPaymentSummaryModalProps) {
  const modalContext = useSelector<State, BuyerPaymentSummaryModalContext>(
    selectBuyerPaymentSummaryModalContext,
  );

  const itemsTableDefaultColDef = useMemo(
    () => ({
      ...globalDefaultColDef,
      filter: false,
      floatingFilter: false,
    }),
    [],
  );

  const { buyerId } = modalContext || {};

  const buyerBalanceSummaryLookup = useSelector(
    selectBuyerBalanceSummaryByBuyerId,
  );

  const summaryData = useMemo(() => {
    if (buyerId == null) {
      return EMPTY_SUMMARY_DATA;
    }
    return buyerBalanceSummaryLookup[buyerId] || EMPTY_SUMMARY_DATA;
  }, [buyerId, buyerBalanceSummaryLookup]);

  const { items, totals } = summaryData;

  const formattedTotals = useMemo(() => {
    const breakdownItems: BreakDownItem[] = [];

    if (totals.amountExclCents > 0) {
      breakdownItems.push({
        label: "Amount excl",
        amount: formatCurrency(totals.amountExclCents),
      });
    }
    if (totals.feesCents > 0) {
      breakdownItems.push({
        label: "Fees",
        amount: formatCurrency(totals.feesCents),
      });
    }
    if (totals.taxCents > 0) {
      breakdownItems.push({
        label: "Tax",
        amount: formatCurrency(totals.taxCents),
      });
    }

    return {
      due: formatCurrency(totals.dueCents),
      breakdownItems,
    };
  }, [totals]);

  if (buyerId == null) {
    return null;
  }

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="md" fullWidth>
      <WaitForSync
        requiredData={[
          ApiModel.SALE_LOTS,
          ApiModel.PAYMENTS,
          ApiModel.BUSINESSES,
          ApiModel.DEPLOYMENTS,
        ]}
      >
        <DialogTitle onClose={onClose}>Payments</DialogTitle>
        <DialogContent dividers>
          <Column>
            <Row alignBaseline marginVertical={1} gridGap={1}>
              <BoldText fontSize="charlie">
                <DeploymentBusinessName businessId={buyerId} />
              </BoldText>
              <Row gridGap={1}>
                <Text fontSize="charlie">Due</Text>
                <BoldText fontSize="charlie">{formattedTotals.due}</BoldText>
              </Row>
            </Row>
            {formattedTotals.breakdownItems.length > 0 && (
              <Row
                marginTop={1}
                marginBottom={2}
                textAlignLeft
                flexWrap
                gridGap={3}
              >
                {formattedTotals.breakdownItems.map(item => (
                  <Row key={item.label} whiteSpaceNoWrap gridGap={1}>
                    <Text fontSize="beta" color="gray44">
                      {item.label}
                    </Text>
                    <Text fontSize="beta" color="black">
                      {item.amount}
                    </Text>
                  </Row>
                ))}
              </Row>
            )}
          </Column>

          <Column>
            <Row justifyBetween alignCenter marginBottom={2} minHeight="60px">
              <Subtitle inline>Items</Subtitle>
            </Row>
            <ModalTableWrapper minHeight={125}>
              <AgGrid
                rowData={items}
                columnDefs={BuyerPaymentSummaryLotColumnDefs}
                defaultColDef={itemsTableDefaultColDef}
                getRowId={getRowId}
                suppressColumnVirtualisation
                suppressScrollOnNewData
              />
            </ModalTableWrapper>
          </Column>

          <Column relative>
            <Row
              justifyBetween
              alignCenter
              marginBottom={3}
              minHeight="40px"
              gridGap={1}
            >
              <Subtitle>Payments</Subtitle>
              <Row flexShrink={0} paddingVertical={1}>
                <Button disabled whiteSpaceNoWrap>
                  Add Payment
                </Button>
              </Row>
            </Row>

            <Column relative>
              <ModalTableWrapper minHeight={150}>
                <WaitForSync requiredData={[ApiModel.PAYMENTS]}>
                  <BusinessPaymentsTable businessId={buyerId} />
                </WaitForSync>
              </ModalTableWrapper>
            </Column>
          </Column>
        </DialogContent>
        <DialogActions>
          <Row justifyCenter fullWidth>
            <SecondaryButton data-tour="close" type="button" onClick={onClose}>
              Close
            </SecondaryButton>
          </Row>
        </DialogActions>
      </WaitForSync>
    </ZoomyDialog>
  );
}
